// frontend/src/components/notes/NotesWorkspace.js
// GPT-5 Phase 1: Notes UI v2 - Split View Architecture
// Ruthless implementation with Apple Notes/Obsidian-level UX

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagProvider';
import MinimalNoteEditor from './MinimalNoteEditor';
// WelcomeFlow moved to AppShell (fires globally, gated on backend
// onboardingCompletedAt) so the walkthrough no longer randomly ambushes
// users just because they happened to click Notes.
import axios from '../../api/axiosConfig';
import { formatDistanceToNow, isToday, isYesterday } from 'date-fns';
import { INITIAL_NOTE } from './constants';
import { markdownService } from '../../services/markdownService';
import { useSidebar } from '../../hooks/useSidebar';
import { DRAWER_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from '../../constants/layout';

// RUTHLESS CONSTANTS - No magic numbers
const SIDEBAR_WIDTH = 320;
const ROW_HEIGHT = 48; // Compact mode only
const SEARCH_DEBOUNCE_MS = 150;
const ANIMATION_DURATION = 300;

// Date grouping logic with precision
const getDateGroup = (date) => {
  const noteDate = new Date(date);
  if (isToday(noteDate)) return 'today';
  if (isYesterday(noteDate)) return 'yesterday';
  return 'older';
};

// Group notes by date with stable sorting
const groupNotesByDate = (notes) => {
  const grouped = {
    today: [],
    yesterday: [],
    older: []
  };
  
  notes.forEach(note => {
    const group = getDateGroup(note.updatedAt || note.createdAt);
    grouped[group].push(note);
  });
  
  return grouped;
};

const NotesWorkspace = () => {
  const { currentUser } = useAuth();
  const featureFlags = useFeatureFlags();
  const isUIv2Enabled = featureFlags.get('notes.ui.v2');

  // Sidebar state management with enterprise features
  const sidebar = useSidebar({
    defaultCollapsed: false,
    persistKey: 'notes.sidebar.collapsed',
    keyboardShortcut: { key: 'b', metaKey: true, ctrlKey: true },
    enableKeyboardShortcut: true,
    animationDuration: ANIMATION_DURATION,
    onStateChange: (collapsed) => {
      // Silent state monitoring for potential future use
    }
  });


  // Core state
  const [notes, setNotes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    hasMore: true,
    loadingMore: false
  });

  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  // Performance refs
  const searchTimeoutRef = useRef(null);
  const notesContainerRef = useRef(null);

  // Load projects for filtering and chips
  const loadProjects = useCallback(async () => {
    if (!currentUser) return;

    try {
      const token = await currentUser.getIdToken();
      const response = await axios.get('/api/projects', {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 1000 }
      });
      const projectsData = response.data.projects || response.data || [];
      setProjects(projectsData);
    } catch (err) {
      console.error('Failed to load projects:', err);
    }
  }, [currentUser]);

  // Load notes with pagination support (Phase 1.3)
  const loadNotes = useCallback(async (forceReload = false) => {
    // Loading notes...
    if (!currentUser) return;

    try {
      if (forceReload) {
        setLoading(true);
        setPagination({
          currentPage: 1,
          hasMore: true,
          loadingMore: false
        });
      }

      const token = await currentUser.getIdToken();
      // Making paginated API call...
      const response = await axios.get('/api/notes', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: 1,
          limit: 50, // Initial load: 50 notes for better performance
          sort: 'updatedAt',
          order: 'desc'
        }
      });

      const rawNotes = response.data.notes || response.data || [];
      // Processing API response...

      const notesData = rawNotes.map(n => ({
        ...n,
        projectIds: n.projectIds || (n.projectId ? [n.projectId] : [])
      }));

      // Notes loaded successfully
      if (forceReload) {
        setNotes(notesData);
      } else {
        setNotes(prev => [...prev, ...notesData]);
      }
      setError('');

      // Update pagination state
      setPagination(prev => ({
        ...prev,
        hasMore: rawNotes.length >= 50, // Assume no more if less than full page
        currentPage: forceReload ? 1 : prev.currentPage
      }));

      // Auto-select first note if none selected
      if (!selectedNote && notesData.length > 0) {
        setSelectedNote(notesData[0]);
      }

    } catch (err) {
      console.error('Failed to load notes:', err);
      setError('Failed to load notes. Please refresh the page.');
    } finally {
      setLoading(false);
      setPagination(prev => ({
        ...prev,
        loadingMore: false
      }));
    }
  }, [currentUser, selectedNote]);

  // Load more notes for pagination
  const loadMoreNotes = useCallback(async () => {
    if (!currentUser || !pagination.hasMore || pagination.loadingMore) return;

    try {
      setPagination(prev => ({
        ...prev,
        loadingMore: true
      }));

      const token = await currentUser.getIdToken();
      const nextPage = pagination.currentPage + 1;

      const response = await axios.get('/api/notes', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: nextPage,
          limit: 50,
          sort: 'updatedAt',
          order: 'desc'
        }
      });

      const rawNotes = response.data.notes || response.data || [];
      const notesData = rawNotes.map(n => ({
        ...n,
        projectIds: n.projectIds || (n.projectId ? [n.projectId] : [])
      }));

      // Append new notes to existing ones
      setNotes(prev => [...prev, ...notesData]);

      // Update pagination state
      setPagination(prev => ({
        ...prev,
        currentPage: nextPage,
        hasMore: rawNotes.length >= 50,
        loadingMore: false
      }));

    } catch (err) {
      console.error('Failed to load more notes:', err);
      setPagination(prev => ({
        ...prev,
        loadingMore: false
      }));
    }
  }, [currentUser, pagination]);

  // Infinite scroll handler
  const handleScroll = useCallback((event) => {
    if (!pagination.hasMore || pagination.loadingMore) return;

    const { scrollTop, scrollHeight, clientHeight } = event.target;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    // Load more when user scrolls to 80% of the content
    if (scrollPercentage >= 0.8) {
      loadMoreNotes();
    }
  }, [pagination.hasMore, pagination.loadingMore, loadMoreNotes]);

  // Initialize on mount
  useEffect(() => {
    loadNotes(true);
    loadProjects();
  }, [loadNotes, loadProjects]);

  // Attach scroll listener for infinite scroll
  useEffect(() => {
    const container = notesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  // Debounced search with performance optimization
  const debouncedSearch = useCallback((query) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(query.toLowerCase().trim());
    }, SEARCH_DEBOUNCE_MS);
  }, []);
  
  // Filter and group notes with memoization
  const { filteredNotes, groupedNotes } = useMemo(() => {
    // Apply filters when needed
    let filtered = notes;

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(note =>
        (note.title?.toLowerCase().includes(searchQuery)) ||
        (note.content?.toLowerCase().includes(searchQuery)) ||
        (note.transcript?.toLowerCase().includes(searchQuery))
      );
    }

    // Apply project filter
    if (projectFilter) {
      filtered = filtered.filter(note => note.projectIds?.includes(projectFilter));
    }

    // Group by date
    const grouped = groupNotesByDate(filtered);

    return { filteredNotes: filtered, groupedNotes: grouped };
  }, [notes, searchQuery, projectFilter]);

  // Map for quick project lookup
  const projectMap = useMemo(() => {
    const map = {};
    projects.forEach(p => {
      map[p._id || p.id] = p;
    });
    return map;
  }, [projects]);

  /**
   * Prepare content for the editor based on note format
   * Handles markdown-to-HTML conversion and format detection
   */
  const prepareNoteContent = useCallback((note) => {
    if (!note) return { content: '', format: 'richtext' };

    const { content, contentHtml, format, transcript } = note;
    const sourceContent = content || transcript || '';

    // Determine the format
    let noteFormat = format || 'markdown';

    // Auto-detect format if not specified
    if (!format) {
      if (contentHtml && contentHtml.trim() !== '') {
        noteFormat = 'richtext';
      } else if (markdownService.isMarkdown(sourceContent)) {
        noteFormat = 'markdown';
      } else {
        noteFormat = 'richtext';
      }
    }

    // Prepare content based on format
    let preparedContent = sourceContent;

    if (noteFormat === 'richtext') {
      // Use HTML content if available, otherwise convert markdown to HTML
      preparedContent = contentHtml || markdownService.markdownToHtml(sourceContent);
    } else if (noteFormat === 'markdown') {
      // Convert markdown to HTML for the rich text editor
      preparedContent = markdownService.markdownToHtml(sourceContent);
      noteFormat = 'richtext'; // Editor will handle it as rich text
    }

    return {
      content: preparedContent,
      format: noteFormat,
      originalFormat: format || 'markdown',
      originalContent: sourceContent
    };
  }, []);
  
  
  // Note selection with instant response
  const selectNote = useCallback((note) => {
    setSelectedNote(note);
  }, []);
  
  // Soft delete with proper state management
  const handleSoftDelete = useCallback(async (note) => {
    if (!window.confirm(`Delete "${note.title || 'Untitled'}"?`)) return;
    
    try {
      const token = await currentUser.getIdToken();
      await axios.delete(`/api/notes/${note._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove from local state
      setNotes(prev => prev.filter(n => n._id !== note._id));
      
      // Select next note
      const noteIndex = filteredNotes.findIndex(n => n._id === note._id);
      const nextNote = filteredNotes[noteIndex + 1] || filteredNotes[noteIndex - 1];
      setSelectedNote(nextNote || null);
      
    } catch (err) {
      console.error('Failed to delete note:', err);
      setError('Failed to delete note');
    }
  }, [currentUser, filteredNotes]);
  
  // Enhanced keyboard navigation system with focus detection
  const [isNotesWorkspaceActive, setIsNotesWorkspaceActive] = useState(true);
  const [isEditorActive, setIsEditorActive] = useState(false);
  const [hasUserManuallyExpanded, setHasUserManuallyExpanded] = useState(false);

  // Enhanced focus detection for editor content
  const detectEditorFocus = useCallback(() => {
    const activeEl = document.activeElement;

    // Strategy 1: Direct element checks
    const isDirectlyFocused = (
      activeEl?.contentEditable === 'true' ||
      activeEl?.classList.contains('ProseMirror') ||
      activeEl?.classList.contains('minimal-editor-content')
    );

    // Strategy 2: Ancestor checks
    const isInEditor = activeEl?.closest('.minimal-editor-content, .ProseMirror');

    // Strategy 3: Title field check
    const isInTitleField = activeEl?.name === 'note-title' ||
                           activeEl?.classList.contains('note-title-input');

    // Strategy 4: Component state (from editor callbacks)
    const isEditorStateActive = isEditorActive;

    return isDirectlyFocused || isInEditor || isInTitleField || isEditorStateActive;
  }, [isEditorActive]);

  // Handle editor focus for auto-collapse
  const handleEditorFocus = useCallback(() => {
    setIsEditorActive(true);

    // Auto-collapse sidebar only if:
    // 1. Sidebar is currently expanded
    // 2. User hasn't manually expanded it during this session
    if (!sidebar.isCollapsed && !hasUserManuallyExpanded) {
      sidebar.collapseTemporary(); // Temporary collapse without localStorage persistence
    }
  }, [sidebar, hasUserManuallyExpanded]);

  const handleEditorBlur = useCallback(() => {
    setIsEditorActive(false);
  }, []);

  // Track manual user actions for sidebar
  const handleManualToggle = useCallback(() => {
    sidebar.toggle();
    setHasUserManuallyExpanded(true);
  }, [sidebar]);

  // Reset manual override when switching notes
  useEffect(() => {
    setHasUserManuallyExpanded(false);
  }, [selectedNote?._id]);

  // Keyboard navigation system with enhanced focus detection
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Component-level check instead of pathname
      if (!isNotesWorkspaceActive) return;

      // Check if user is actively editing content
      const isEditingContent = detectEditorFocus();

      // If editing, only handle special key combinations
      if (isEditingContent) {
        if ((e.key === 'Delete' || e.key === 'Backspace') &&
            (e.ctrlKey || e.metaKey) && selectedNote) {
          e.preventDefault();
          handleSoftDelete(selectedNote);
        }
        return; // Let editor handle all other keys
      }

      // Handle note navigation when NOT editing
      const currentIndex = filteredNotes.findIndex(note => note._id === selectedNote?._id);

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          if (currentIndex > 0) {
            selectNote(filteredNotes[currentIndex - 1]);
          }
          break;

        case 'ArrowDown':
          e.preventDefault();
          if (currentIndex < filteredNotes.length - 1) {
            selectNote(filteredNotes[currentIndex + 1]);
          }
          break;

        case 'Enter':
          if (!e.ctrlKey && !e.metaKey && selectedNote) {
            e.preventDefault();
            // Focus the editor and trigger auto-collapse
            document.querySelector('.ProseMirror')?.focus();
            handleEditorFocus();
          }
          break;

        default:
          break;
      }
    };

    // Manage component lifecycle properly
    setIsNotesWorkspaceActive(true);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      setIsNotesWorkspaceActive(false);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isNotesWorkspaceActive, detectEditorFocus, filteredNotes, selectedNote, selectNote, handleSoftDelete, handleEditorFocus]);
  
  // Handle note save/update
  const handleNoteSaved = useCallback((savedNote) => {
    setNotes(prev => {
      const index = prev.findIndex(n => n._id === savedNote._id);
      if (index >= 0) {
        // Update existing note
        const updated = [...prev];
        updated[index] = savedNote;
        return updated;
      } else {
        // Add new note
        return [savedNote, ...prev];
      }
    });
    
    setSelectedNote(savedNote);
  }, []);
  
  // Utility to strip HTML tags - memoized for performance
  const toPlain = useCallback((s = '') => s.replace(/<[^>]*>/g, ''), []);

  // Render note row with optimized styling
  const renderNoteRow = useCallback((note, index) => {
    const isSelected = selectedNote?._id === note._id;
    const rowHeight = ROW_HEIGHT;

    // Smart content preview - markdown-aware
    const raw = note.markdown ?? note.content ?? note.text ?? note.transcript ?? '';
    const plain = toPlain(raw);
    const firstLine = plain.split('\n')[0]?.trim() || '';
    const truncatedPreview = firstLine.length > 60 ? firstLine.substring(0, 60) + '...' : firstLine;

    const projectIds = note.projectIds || [];
    const projectObjs = projectIds.map(pid => projectMap[pid]).filter(Boolean);
    const displayedProjects = projectObjs.slice(0, 3);
    const overflowProjects = projectObjs.slice(3);

    return (
      <ListItem
        key={note._id}
        button={true}
        selected={isSelected}
        onClick={() => selectNote(note)}
        sx={{
          height: rowHeight,
          px: 2,
          py: 0.5,
          borderLeft: isSelected ? '3px solid' : '3px solid transparent',
          borderLeftColor: 'primary.main',
          bgcolor: isSelected ? 'action.selected' : 'transparent',
          '&:hover': {
            bgcolor: isSelected ? 'action.selected' : 'action.hover',
          },
          cursor: 'pointer',
          transition: 'all 0.1s ease-in-out',
        }}
      >
        <ListItemText
          primary={
            <Typography
              variant="body2"
              fontWeight={isSelected ? 600 : 500}
              sx={{
                fontSize: '14px',
                lineHeight: 1.3,
                color: isSelected ? 'primary.main' : 'text.primary',
                mb: 0,
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {note.title?.trim() || firstLine || 'Untitled'}
            </Typography>
          }
          secondary={
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '11px' }}
                >
                  {(() => {
                    try {
                      const dateValue = note.updatedAt || note.createdAt;
                      if (!dateValue) return 'Unknown time';
                      const date = new Date(dateValue);
                      if (isNaN(date.getTime())) return 'Invalid date';
                      return formatDistanceToNow(date, { addSuffix: true });
                    } catch (error) {
                      console.warn('Date formatting error:', error, note);
                      return 'Unknown time';
                    }
                  })()}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  {displayedProjects.map(project => (
                    <Chip
                      key={project._id || project.id}
                      label={project.name}
                      size="small"
                      sx={{
                        height: 16,
                        fontSize: '10px',
                        bgcolor: project.color || 'primary.main',
                        color: '#fff',
                        '& .MuiChip-label': { px: 0.5 }
                      }}
                    />
                  ))}
                  {overflowProjects.length > 0 && (
                    <Tooltip title={overflowProjects.map(p => p.name).join(', ')}>
                      <Chip
                        label={`+${overflowProjects.length}`}
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: '10px',
                          '& .MuiChip-label': { px: 0.5 }
                        }}
                      />
                    </Tooltip>
                  )}
                  {note.metadata?.labels && note.metadata.labels.length > 0 && (
                    note.metadata.labels.slice(0, 1).map(tag => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: '10px',
                          '& .MuiChip-label': { px: 0.5 }
                        }}
                      />
                    ))
                  )}
                </Box>
              </Box>
            </Box>
          }
          secondaryTypographyProps={{ component: 'div' }}
        />
      </ListItem>
    );
  }, [selectedNote, selectNote, projectMap, toPlain]);
  
  // Render date group
  const renderDateGroup = useCallback((title, notes, isExpanded = true) => {
    if (notes.length === 0) {
      return null;
    }
    
    return (
      <Box key={title} sx={{ mb: 1 }}>
        <Typography
          variant="overline"
          sx={{
            px: 2,
            py: 1,
            display: 'block',
            fontSize: '11px',
            fontWeight: 600,
            color: 'text.secondary',
            letterSpacing: '0.5px',
          }}
        >
          {title} ({notes.length})
        </Typography>
        <List disablePadding>
          {notes.map((note, index) => renderNoteRow(note, index))}
        </List>
        {title !== 'OLDER' && <Divider sx={{ my: 1, mx: 2 }} />}
      </Box>
    );
  }, [renderNoteRow]);
  
  if (!isUIv2Enabled) {
    // Fallback to existing Dashboard component
    return null; // Will be handled by parent routing
  }
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: 'background.default' }}>
      {/* LEFT SIDEBAR - Notes List */}
      <Paper
        elevation={0}
        sx={{
          ...sidebar.getSidebarStyles(SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH),
          borderRight: 1,
          borderColor: 'divider',
          display: 'flex',
          position: 'relative',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          // Prevent pointer events when collapsed so expand button is clickable
          pointerEvents: sidebar.isCollapsed ? 'none' : 'auto',
        }}
      >
        {/* Sidebar Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: '18px' }}>
              Notes
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title={`Collapse sidebar (${sidebar.shortcut})`}>
                <IconButton size="small" onClick={handleManualToggle}>
                  <ChevronLeftIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="New note">
                <IconButton size="small" onClick={() => setSelectedNote(INITIAL_NOTE)}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search notes..."
            onChange={(e) => debouncedSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '14px',
              },
            }}
          />
          {projects.length > 0 && (
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <Select
                value={projectFilter}
                displayEmpty
                onChange={(e) => setProjectFilter(e.target.value)}
                renderValue={(selected) => {
                  if (!selected) return 'All Projects';
                  const proj = projectMap[selected];
                  return proj ? `Project: ${proj.name}` : 'All Projects';
                }}
              >
                <MenuItem value="">
                  <em>All Projects</em>
                </MenuItem>
                {projects.map(project => (
                  <MenuItem key={project._id || project.id} value={project._id || project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        
        {/* Notes List */}
        <Box sx={{ flex: 1, overflow: 'auto' }} ref={notesContainerRef}>
          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}
          
          {filteredNotes.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
              <Typography color="text.secondary">
                {searchQuery ? 'No notes found' : 'No notes yet'}
              </Typography>
              {!searchQuery && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  sx={{ mt: 2 }}
                  onClick={() => setSelectedNote(INITIAL_NOTE)}
                >
                  Create your first note
                </Button>
              )}
            </Box>
          )}
          
          {/* Date-grouped notes */}
          {renderDateGroup('TODAY', groupedNotes.today)}
          {renderDateGroup('YESTERDAY', groupedNotes.yesterday)}
          {renderDateGroup('OLDER', groupedNotes.older)}

          {/* Load More Button for Pagination */}
          {pagination.hasMore && (
            <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
              <Button
                variant="outlined"
                onClick={loadMoreNotes}
                disabled={pagination.loadingMore}
                startIcon={
                  pagination.loadingMore ? (
                    <CircularProgress size={18} />
                  ) : null
                }
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '13px',
                  fontWeight: 500,
                  borderColor: 'divider',
                  color: 'text.secondary',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover'
                  }
                }}
              >
                {pagination.loadingMore ? 'Loading...' : 'Load More Notes'}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* RIGHT PANE - Editor */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {selectedNote ? (() => {
          const preparedNote = prepareNoteContent(selectedNote);
          return (
            <MinimalNoteEditor
              key={selectedNote._id}
              noteId={selectedNote._id}
              initialTitle={selectedNote.title}
              initialContent={preparedNote.content}
              initialFormat={preparedNote.format}
              originalFormat={preparedNote.originalFormat}
              onSave={handleNoteSaved}
              onCancel={() => {}}
              onEditorFocus={handleEditorFocus}
              onEditorBlur={handleEditorBlur}
            />
          );
        })() : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h5"
              fontWeight={600}
              sx={{ mb: 2, fontSize: '24px', lineHeight: 1.6 }}
            >
              Create a new note
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 4, maxWidth: 400 }}>
              Select a note from the sidebar or start writing to create a new one
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => setSelectedNote(INITIAL_NOTE)}
            >
              New Note
            </Button>
          </Box>
        )}
      </Box>

      {/* Sidebar Expand Button - Outside flex layout for proper z-index */}
      {sidebar.isCollapsed && (
        <Box
          sx={{
            position: 'fixed',
            left: DRAWER_WIDTH + SIDEBAR_COLLAPSED_WIDTH + 16, // Account for navigation drawer + collapsed sidebar
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: (theme) => theme.zIndex.drawer + 2, // Higher z-index than drawer
            pointerEvents: 'auto', // Ensure button is clickable
          }}
        >
          <Tooltip title={`Expand sidebar (${sidebar.shortcut})`} placement="right">
            <IconButton
              size="small"
              onClick={handleManualToggle}
              sx={{
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: '0 8px 8px 0',
                borderLeft: 'none',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
                boxShadow: 2,
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Welcome flow trigger moved to AppShell (global, gated on
          backend onboardingCompletedAt). */}
    </Box>
  );
};

export default NotesWorkspace;
